import React, { useEffect, useCallback, useState } from "react";
import { toast } from "react-toastify";
import api from "../../../features/auth/axiosConfig";
import EditMenu from "./EditMenu";
import CustomerToolbar from "./customers/components/CustomerToolbar";
import CustomerTable from "./customers/components/CustomerTable";
import LoadingState from "./customers/components/LoadingState";
import ErrorState from "./customers/components/ErrorState";
import { useCustomerData } from "./customers/hooks/useCustomerData";
import { useCustomerSearch } from "./customers/hooks/useCustomerSearch";
import { usePagination } from "./customers/hooks/usePagination";
import { useCustomerDialogs } from "./customers/hooks/useCustomerDialogs";
import { CustomerData as CustomerDataType } from "./customers/types/customerTypes";
import CustomerDialogs from "./customers/components/CustomerDialogs";
import { PageTitle } from "src/shared/PageTitle";
import LoadingScreen from "src/shared/components/LoadingScreen";

const CustomerDataPage: React.FC = () => {
  const { page, rowsPerPage, handleChangePage, handleChangeRowsPerPage, setPage, } = usePagination(10);
  const { selectedCustomer, isEditOpen, isDeleteOpen, isAddOpen, openEdit, openDelete, openAdd, closeEdit, closeDelete, closeAdd, resetSelection } = useCustomerDialogs();
  const [, setBusinessType] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [showDeleted, setShowDeleted] = useState(false);
  const { customerData, loading, error, refetch } = useCustomerData(showDeleted, searchTerm, page, rowsPerPage);
  const { filteredData } = useCustomerSearch(customerData?.customer_details, showDeleted);

  useEffect(() => {
    document.title = "Customers › Settings › iMetanic";
    setBusinessType(sessionStorage.getItem("business_type") || "");
  }, []);
  const handleSearch = useCallback(
    (term: string) => {
      setSearchTerm(term);
      setPage(0);
    },
    [setPage]
  );

  const handleToggleDeleted = useCallback(() => {
    setShowDeleted((prev) => !prev);
    setSearchTerm("");
    setPage(0);
  }, [setPage]);

  const handleAddCustomer = useCallback(
    async (customerData: Partial<CustomerDataType>) => {
      try {
        const response = await api.post("/app/v1/pos/customer", customerData);
        if (response.data) {
          toast.success("Customer added successfully");
          closeAdd();
          setSearchTerm("");

          refetch();
        }
      } catch (err: any) {
        const message = err.response?.data?.message || (err.response?.status === 409 ? "Duplicate entry" : "Error adding customer");
        toast.error(message);
        console.error("Error adding customer:", err);
      }
    },
    [refetch, closeAdd]
  );

  const handleCustomerTablePageChange = (newPage: number) => {
    handleChangePage(null, newPage);
  };

  const handleCustomerTableRowsChange = (newRowsPerPage: number) => {
    handleChangeRowsPerPage({
      target: { value: newRowsPerPage.toString() },
    } as React.ChangeEvent<HTMLInputElement>);
    setPage(0);
  };

  return (
    <div>
      {/* <HeaderRenderer businessType={businessType} /> */}
      {/* <div style={{ display: "flex" }}> */}
      {/* <MenuRenderer businessType={businessType} /> */}
      <div className="setting-overall-container">
        <PageTitle title="- Settings › Customers" />
        <EditMenu editmenu={{ Customer: "customer" }} />

        {/* Toolbar */}
        <div className='customer-add-container'>
          <CustomerToolbar
            searchTerm={searchTerm}
            showDeleted={showDeleted}
            onSearchChange={handleSearch}
            onToggleDeleted={handleToggleDeleted}
            onAddCustomer={openAdd}
          />
        </div>

        {/* States */}
        {loading && <LoadingState />}
        {error && <ErrorState message={error} />}

        {!loading && !error && (
          <CustomerTable
            data={filteredData || []}
            page={page}
            rowsPerPage={rowsPerPage}
            totalRecords={customerData?.totalRecords || 0}
            showDeleted={showDeleted}
            onEdit={openEdit}
            onDelete={(id, name) => openDelete({ _id: { $oid: id }, Name: name } as CustomerDataType)}
            onPageChange={handleCustomerTablePageChange}
            onRowsPerPageChange={handleCustomerTableRowsChange}
          />
        )}

        {/* Dialogs */}
        <CustomerDialogs
          isEditOpen={isEditOpen}
          isDeleteOpen={isDeleteOpen}
          isAddOpen={isAddOpen}
          selectedCustomer={selectedCustomer}
          onCloseEdit={closeEdit}
          onCloseDelete={closeDelete}
          onCloseAdd={closeAdd}
          onAddCustomer={handleAddCustomer}
          onRefetch={refetch}
          onResetSelection={resetSelection}
        />
        {loading && <LoadingScreen />}
        {/* <ToastContainer /> */}
      </div>
    </div>
    // </div>
  );
};

export default CustomerDataPage;
